import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineLoading3Quarters, AiOutlineEdit, AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import TeamMemberEditor from './TeamMemberEditor';
import apiService from '../services/apiService';
import {
  ContentCard,
  CardTitle,
  ServicesList,
  ServiceItem,
  ServiceHeader,
  DeleteButton,
  AddButton,
  ErrorMessage
} from '../styles/AdminStyles';
import styled from 'styled-components';

// Additional styled components for team member management
const TeamMembersList = styled(ServicesList)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const TeamMemberItem = styled(ServiceItem)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const TeamMemberHeader = styled(ServiceHeader)`
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f5f5f5;
`;

const TeamMemberContent = styled.div`
  padding: 15px;
`;

const TeamMemberImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TeamMemberDetails = styled.div`
  margin-top: 10px;
`;

const TeamMemberPosition = styled.p`
  color: #666;
  font-style: italic;
  margin: 5px 0;
`;

const TeamMemberContact = styled.div`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  .loading-icon {
    animation: spin 1s linear infinite;
    font-size: 2rem;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: ${props => props.color || '#333'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.hoverColor || '#000'};
  }
`;

const TeamMemberManager = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getTeamMembers();
      setTeamMembers(data);
      setError('');
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const handleAddMember = () => {
    setEditingMember(null);
    setIsAdding(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsAdding(true);
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }

    setLoading(true);
    try {
      await apiService.deleteTeamMember(id);
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error deleting team member:', err);
      setError('Failed to delete team member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMember = async (memberData) => {
    try {
      if (editingMember?._id) {
        // Update existing member
        await apiService.updateTeamMember(editingMember._id, memberData);
      } else {
        // Add new member
        await apiService.createTeamMember(memberData);
      }
      
      // Refresh the list
      await fetchTeamMembers();
      
      // Close the editor
      setIsAdding(false);
      setEditingMember(null);
      
      return true;
    } catch (err) {
      console.error('Error saving team member:', err);
      throw new Error('Failed to save team member. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setIsAdding(false);
    setEditingMember(null);
  };

  // Loading state
  if (loading && !isAdding && teamMembers.length === 0) {
    return (
      <ContentCard>
        <CardTitle>Team Management</CardTitle>
        <LoadingContainer>
          <AiOutlineLoading3Quarters className="loading-icon" />
        </LoadingContainer>
      </ContentCard>
    );
  }

  // Editor view
  if (isAdding) {
    return (
      <TeamMemberEditor 
        teamMember={editingMember} 
        onSave={handleSaveMember} 
        onCancel={handleCancelEdit} 
      />
    );
  }

  // List view
  return (
    <ContentCard>
      <CardTitle>Team Management</CardTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <AddButton onClick={handleAddMember}>
        <AiOutlinePlus /> Add Team Member
      </AddButton>
      
      <TeamMembersList>
        {teamMembers.map((member) => (
          <TeamMemberItem key={member._id}>
            <TeamMemberHeader>
              <h3>{member.name}</h3>
              <ActionButtonsContainer>
                <ActionButton 
                  onClick={() => handleEditMember(member)}
                  color="#4CAF50"
                  hoverColor="#2E8B57"
                >
                  <AiOutlineEdit />
                </ActionButton>
                <ActionButton 
                  onClick={() => handleDeleteMember(member._id)}
                  color="#F44336"
                  hoverColor="#D32F2F"
                >
                  <AiOutlineDelete />
                </ActionButton>
              </ActionButtonsContainer>
            </TeamMemberHeader>
            
            <TeamMemberImageContainer>
              {member.imageUrl ? (
                <LazyLoadImage
                  src={member.imageUrl}
                  alt={member.name}
                  effect="blur"
                  width="100%"
                  height="100%"
                  style={{ objectFit: 'cover' }}
                  placeholder={
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%'
                    }}>
                      <AiOutlineLoading3Quarters className="loading-icon" />
                    </div>
                  }
                />
              ) : (
                <div style={{ fontSize: '3rem' }}>👤</div>
              )}
            </TeamMemberImageContainer>
            
            <TeamMemberContent>
              <TeamMemberDetails>
                <TeamMemberPosition>{member.position}</TeamMemberPosition>
                {member.bio && <p>{member.bio}</p>}
                
                <TeamMemberContact>
                  {member.email && (
                    <div>Email: {member.email}</div>
                  )}
                  {member.linkedin && (
                    <div>LinkedIn: {member.linkedin.substring(0, 30)}...</div>
                  )}
                </TeamMemberContact>
              </TeamMemberDetails>
            </TeamMemberContent>
          </TeamMemberItem>
        ))}
      </TeamMembersList>
    </ContentCard>
  );
};

export default TeamMemberManager;
